export const imageIsAr = document.documentElement.lang === 'ar';

export function imageName(name) {
  return name.replace(/\.[^.]+$/, '').replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 100) || 'image';
}

export function imageExtension(mime) {
  return ({
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/avif': 'avif',
    'image/bmp': 'bmp',
    'image/x-icon': 'ico',
    'image/vnd.microsoft.icon': 'ico',
    'image/gif': 'gif',
    'image/tiff': 'tiff',
    'application/pdf': 'pdf',
    'image/x-tga': 'tga'
  })[mime] || 'png';
}

export async function openBitmap(file, maxPixels = Infinity) {
  let sourceBlob = file;
  const isHeic = file.type === 'image/heic' || 
                 file.type === 'image/heif' || 
                 /\.(heic|heif)$/i.test(file.name || '');

  if (isHeic) {
    try {
      const heicModule = await import('heic2any');
      const heic2any = heicModule.default || heicModule;
      const converted = await heic2any({ blob: file, toType: 'image/png' });
      sourceBlob = Array.isArray(converted) ? converted[0] : converted;
    } catch (err) {
      console.warn('HEIC decoding failed, trying native decoder:', err);
    }
  }

  let bitmap;
  try { bitmap = await createImageBitmap(sourceBlob); }
  catch { throw new Error(imageIsAr ? 'تعذر فتح الصورة في المتصفح.' : 'The browser could not open this image.'); }
  if (Number.isFinite(maxPixels) && bitmap.width * bitmap.height > maxPixels) {
    bitmap.close();
    throw new Error(imageIsAr ? 'أبعاد الصورة أكبر من الحد الآمن للمعالجة.' : 'Image dimensions exceed the safe processing limit.');
  }
  return bitmap;
}

export function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error(imageIsAr ? 'تعذر تصدير الصورة بهذا التنسيق في متصفحك.' : 'This browser could not export the image in that format.'));
  }, type, quality));
}

export function makeCanvas(width, height, background = null) {
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d', { alpha: background !== '#ffffff' });
  if (!context) throw new Error(imageIsAr ? 'تعذر تهيئة مساحة الصورة.' : 'Could not initialize the image canvas.');
  if (background) { context.fillStyle = background; context.fillRect(0, 0, width, height); }
  return { canvas, context };
}

export function nextFrame() { return new Promise((resolve) => requestAnimationFrame(resolve)); }

export function canvasToBMPBlob(canvas) {
  const ctx = canvas.getContext('2d');
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const width = canvas.width;
  const height = canvas.height;
  const rowSize = width * 4;
  const pixelArraySize = rowSize * height;
  const fileSize = 54 + pixelArraySize;
  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);

  // BM Header
  view.setUint16(0, 0x424D, false); // 'BM'
  view.setUint32(2, fileSize, true);
  view.setUint32(6, 0, true);
  view.setUint32(10, 54, true); // data offset

  // DIB Header (BITMAPINFOHEADER)
  view.setUint32(14, 40, true);
  view.setInt32(18, width, true);
  view.setInt32(22, -height, true); // top-down
  view.setUint16(26, 1, true); // 1 plane
  view.setUint16(28, 32, true); // 32 bpp
  view.setUint32(30, 0, true); // BI_RGB
  view.setUint32(34, pixelArraySize, true);
  view.setInt32(38, 2835, true); // ~72 DPI
  view.setInt32(42, 2835, true);
  view.setUint32(46, 0, true);
  view.setUint32(50, 0, true);

  const src = imgData.data;
  const dst = new Uint8Array(buffer, 54);
  for (let i = 0, j = 0; i < src.length; i += 4, j += 4) {
    dst[j] = src[i + 2];     // B
    dst[j + 1] = src[i + 1]; // G
    dst[j + 2] = src[i];     // R
    dst[j + 3] = src[i + 3]; // A
  }

  return new Blob([buffer], { type: 'image/bmp' });
}

export function canvasToTGABlob(canvas) {
  const ctx = canvas.getContext('2d');
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const width = canvas.width;
  const height = canvas.height;
  const buffer = new ArrayBuffer(18 + width * height * 4);
  const view = new DataView(buffer);

  view.setUint8(2, 2); // Uncompressed true-color
  view.setUint16(12, width, true);
  view.setUint16(14, height, true);
  view.setUint8(16, 32); // 32 bpp
  view.setUint8(17, 8 | 32); // 8-bit alpha, origin top-left

  const src = imgData.data;
  const dst = new Uint8Array(buffer, 18);
  for (let i = 0, j = 0; i < src.length; i += 4, j += 4) {
    dst[j] = src[i + 2];     // B
    dst[j + 1] = src[i + 1]; // G
    dst[j + 2] = src[i];     // R
    dst[j + 3] = src[i + 3]; // A
  }

  return new Blob([buffer], { type: 'image/x-tga' });
}

export function canvasToTIFFBlob(canvas) {
  const ctx = canvas.getContext('2d');
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const width = canvas.width;
  const height = canvas.height;
  const pixelBytes = width * height * 4;
  const dataOffset = 256;
  const totalSize = dataOffset + pixelBytes;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);

  // TIFF Header
  view.setUint16(0, 0x4949, true); // Little-endian 'II'
  view.setUint16(2, 42, true);     // TIFF version
  view.setUint32(4, 8, true);      // First IFD offset

  // IFD (13 entries)
  const numEntries = 13;
  view.setUint16(8, numEntries, true);

  const bitsPerSampleOffset = 8 + 2 + (numEntries * 12) + 4; // 170
  const xResOffset = bitsPerSampleOffset + 8; // 178
  const yResOffset = xResOffset + 8; // 186

  let ifdPtr = 10;
  const writeTag = (tag, type, count, valOrOffset) => {
    view.setUint16(ifdPtr, tag, true);
    view.setUint16(ifdPtr + 2, type, true);
    view.setUint32(ifdPtr + 4, count, true);
    view.setUint32(ifdPtr + 8, valOrOffset, true);
    ifdPtr += 12;
  };

  writeTag(0x0100, 4, 1, width);
  writeTag(0x0101, 4, 1, height);
  writeTag(0x0102, 3, 4, bitsPerSampleOffset);
  writeTag(0x0103, 3, 1, 1); // Uncompressed
  writeTag(0x0106, 3, 1, 2); // RGB
  writeTag(0x0111, 4, 1, dataOffset);
  writeTag(0x0115, 3, 1, 4); // RGBA
  writeTag(0x0116, 4, 1, height);
  writeTag(0x0117, 4, 1, pixelBytes);
  writeTag(0x011A, 5, 1, xResOffset);
  writeTag(0x011B, 5, 1, yResOffset);
  writeTag(0x0128, 3, 1, 2); // Inch
  writeTag(0x0152, 3, 1, 2); // Unassociated alpha

  view.setUint32(ifdPtr, 0, true); // End of IFD

  // BitsPerSample
  view.setUint16(bitsPerSampleOffset, 8, true);
  view.setUint16(bitsPerSampleOffset + 2, 8, true);
  view.setUint16(bitsPerSampleOffset + 4, 8, true);
  view.setUint16(bitsPerSampleOffset + 6, 8, true);

  // XResolution (72/1)
  view.setUint32(xResOffset, 72, true);
  view.setUint32(xResOffset + 4, 1, true);

  // YResolution (72/1)
  view.setUint32(yResOffset, 72, true);
  view.setUint32(yResOffset + 4, 1, true);

  // Pixel data (RGBA)
  const destArray = new Uint8Array(buffer, dataOffset, pixelBytes);
  destArray.set(imgData.data);

  return new Blob([buffer], { type: 'image/tiff' });
}

export async function canvasToPDFBlob(canvas) {
  const { PDFDocument } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.create();
  const pngBlob = await canvasToBlob(canvas, 'image/png');
  const pngBytes = await pngBlob.arrayBuffer();
  const pngImage = await pdfDoc.embedPng(pngBytes);
  const page = pdfDoc.addPage([canvas.width, canvas.height]);
  page.drawImage(pngImage, {
    x: 0,
    y: 0,
    width: canvas.width,
    height: canvas.height
  });
  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
}

export async function createIcoBlob(canvasList) {
  const imagesData = [];
  for (const canvas of canvasList) {
    const pngBlob = await canvasToBlob(canvas, 'image/png');
    const pngBytes = new Uint8Array(await pngBlob.arrayBuffer());
    imagesData.push({
      width: canvas.width >= 256 ? 0 : canvas.width,
      height: canvas.height >= 256 ? 0 : canvas.height,
      data: pngBytes
    });
  }

  const headerSize = 6;
  const dirEntrySize = 16;
  const numImages = imagesData.length;
  let offset = headerSize + (numImages * dirEntrySize);

  let totalSize = offset;
  for (const img of imagesData) {
    totalSize += img.data.length;
  }

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);

  // ICONDIR header
  view.setUint16(0, 0, true);
  view.setUint16(2, 1, true); // 1 = ICO
  view.setUint16(4, numImages, true);

  let dirOffset = 6;
  let dataOffset = offset;

  for (const img of imagesData) {
    view.setUint8(dirOffset + 0, img.width);
    view.setUint8(dirOffset + 1, img.height);
    view.setUint8(dirOffset + 2, 0);
    view.setUint8(dirOffset + 3, 0);
    view.setUint16(dirOffset + 4, 1, true);
    view.setUint16(dirOffset + 6, 32, true);
    view.setUint32(dirOffset + 8, img.data.length, true);
    view.setUint32(dirOffset + 12, dataOffset, true);

    const targetArr = new Uint8Array(buffer, dataOffset, img.data.length);
    targetArr.set(img.data);

    dirOffset += 16;
    dataOffset += img.data.length;
  }

  return new Blob([buffer], { type: 'image/x-icon' });
}
